import { Box, Flex, Tag, TagCloseButton, TagLabel, Tooltip } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { GroupBase } from 'chakra-react-select';
import { ReactNode, useState } from 'react';
import { z } from 'zod/v4';

import { Combobox } from '../combobox/combobox';
import { ComboboxComponents } from '../combobox/combobox-components';
import { withMultiCombobox } from '../combobox/multi-combobox';
import { VirtualizedOptions } from '../combobox/virtualized-options';
import { AvatarOptionIcon } from '../editable/avatar-option';
import { AvatarTooltip } from '../editable/avatar-tooltip';
import { defaultProps as avatarDefaults } from '../editable/editable-multiselect-avatar';

const personSchema = z.object({
  type: z.literal('user'),
  id: z.string(),
  displayName: z.string(),
});

const teamSchema = z.object({
  type: z.literal('team'),
  id: z.string(),
  displayName: z.string(),
  memberCount: z.number(),
  members: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
    }),
  ),
});

export const GroupedOptionSchema = z.union([personSchema, teamSchema]);

type Option = z.infer<typeof GroupedOptionSchema>;
export type PersonOption = z.infer<typeof personSchema>;
export type TeamOption = z.infer<typeof teamSchema>;
type OptionGroup = GroupBase<Option>;

function VirtualizedGroupOptions({ children }: { children: ReactNode }) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  return (
    <Box ref={setScrollElement} maxH="220px" overflowY="auto" overscrollBehavior="contain">
      <VirtualizedOptions
        scrollElement={scrollElement}
        viewportHeight={220}
        paddingStart={0}
        paddingEnd={0}
      >
        {children}
      </VirtualizedOptions>
    </Box>
  );
}

const selectComponents: ComboboxComponents<Option> = {
  Option: (opt) => {
    if (opt.type === 'team') {
      return (
        <Tooltip
          label={
            <AvatarTooltip
              values={opt.members.map((member) => ({
                id: member.id,
                displayName: member.displayName,
              }))}
            />
          }
          isDisabled={opt.members.length === 0}
          placement="bottom"
          py={2}
          hasArrow
          openDelay={500}
          gutter={12}
        >
          <Flex gap={2} alignItems="center" w="full">
            <AvatarOptionIcon kind="team" displayName={opt.displayName} size="xs" />
            <Text size="xs" weight="medium" color="muted" isGrow>
              {opt.displayName}
            </Text>
            <Text size="xs" color="muted">
              {opt.memberCount} members
            </Text>
          </Flex>
        </Tooltip>
      );
    }

    return avatarDefaults.components.Option(opt);
  },

  SelectedOption: (opt) => {
    if (opt.type === 'team') {
      const { displayName, menuIsOpen, removeProps } = opt;
      return (
        <Tag bg="none" minW="auto" maxW="none" px={0}>
          <AvatarOptionIcon kind="team" displayName={displayName} size="xs" />
          {menuIsOpen && (
            <TagLabel ml={2} fontSize="xs" color="gray.500">
              {displayName}
            </TagLabel>
          )}
          {menuIsOpen && removeProps && (
            <Box as={TagCloseButton} {...removeProps} aria-label={`Remove ${displayName}`} />
          )}
        </Tag>
      );
    }

    return avatarDefaults.components.SelectedOption(opt);
  },

  OptionContainer: avatarDefaults.components.OptionContainer,

  Group: ({ label, children, index }) => (
    <Box overflow="hidden" borderTopWidth={index > 0 ? '1px' : 0} borderColor="gray.200">
      <Box px={3} pt={3} pb={2}>
        <Text size="xs" color="muted" weight="medium">
          {label}
        </Text>
      </Box>
      <VirtualizedGroupOptions>{children}</VirtualizedGroupOptions>
    </Box>
  ),

  MenuList: ({
    children,
    innerRef,
    innerProps,
  }: {
    children: ReactNode;
    innerRef: React.Ref<HTMLDivElement>;
    innerProps: JSX.IntrinsicElements['div'];
    maxHeight: number;
  }) => (
    <Box
      ref={innerRef}
      {...innerProps}
      bg="chakra-body-bg"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="chakra-border-color"
      borderTop="none"
      borderTopRadius={0}
      borderBottomRadius="md"
    >
      {children}
    </Box>
  ),
};

export function GroupedSelect({
  inputId,
  'aria-labelledby': ariaLabelledBy,
  'aria-errormessage': ariaErrorMessage,
  isInvalid,
  required,
  options,
  value,
  onChange,
  isLoading,
  placeholder,
  isDisabled,
}: {
  inputId?: string;
  'aria-labelledby'?: string;
  'aria-errormessage'?: string;
  isInvalid?: boolean;
  required?: boolean;
  options: OptionGroup[];
  value: Option[] | undefined;
  onChange: (values: Option[]) => void;
  isLoading?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
}) {
  return (
    <Combobox<Option, true, GroupBase<Option>>
      inputId={inputId}
      aria-labelledby={ariaLabelledBy}
      aria-errormessage={ariaErrorMessage}
      isInvalid={isInvalid}
      required={required}
      {...withMultiCombobox({
        isMulti: true,
        options,
        value: value ?? [],
        onChange: (newValue) => onChange([...newValue]),
        isLoading,
        placeholder,
        isDisabled,
        getOptionValue: (opt) => opt.id,
        getOptionLabel: (opt) => opt.displayName,
      })}
      variant="outline"
      menuPosition="fixed"
      captureMenuScroll={false}
      components={selectComponents}
    />
  );
}
